import type {
  AllMembershipPlanType,
  ApiHookReturn,
  FirstPurchaseData,
  MarketedMembershipPlan,
  MarketedMembershipPlanType,
  MembershipPlanShape,
  PurchaseType,
} from '@frontend/api-client';
import type { CreditPackage } from '@frontend/api-types';
import type { MutationHandler } from '@frontend/duck-tape';
import { P, match } from '@frontend/duck-tape';
import type { TypedSegmentTrack } from '@frontend/react';
import { createContext, useCallback, useEffect, useMemo, useRef, useState, useTempFeatureFlag } from '@frontend/react';
import { useDefaultPromoCode } from '../useDefaultPromoCode';
import type { PromotionTranslationActions, PromotionTranslationState } from '../usePromotionTranslation';
import { usePromotionTranslation } from '../usePromotionTranslation';
import type { OnConfirmPaymentTypeOption } from './types';

const PaywallContext = createContext<PaywallContextValue | undefined>(undefined);

export type PaywallStep = 'checkout' | 'selectPurchaseType' | 'selectSubscriptionPlan' | 'success';

export type PaywallState = {
  navigation: {
    canDecrement: boolean;
    canIncrement: boolean;
    currentStep: MaybeUndefined<PaywallStep>;
    currentStepIndex: number;
    paywallSessionId: MaybeUndefined<string>;
    shouldCompleteOnboardingOnClose: boolean;
    showDismissButton: boolean;
    steps: PaywallStep[];
  };
  promotionTranslation: PromotionTranslationState;
  purchase: FirstPurchaseData & {
    selectedCreditsPackage: MaybeUndefined<CreditPackage>;
    selectedPlanData: MaybeUndefined<MarketedMembershipPlan>;
    selectedPlanType: MaybeUndefined<MarketedMembershipPlanType>;
  };
};

export type PaywallActions = {
  navigation: {
    decrementStep: () => void;
    handleBackButton: () => void;
    incrementStep: () => void;
  };
  promotionTranslation: PromotionTranslationActions;
  purchase: {
    onConfirmSubscriptionChoice: (selectedPlanType: MarketedMembershipPlanType) => void;
    onFinish: () => Promise<void> | void;
    onSelectPaymentType: (option: OnConfirmPaymentTypeOption) => void;
    onSelectPlan: (plan: MarketedMembershipPlanType) => void;
    onSkipPurchase: () => void;
  };
  resetAllState: () => void;
  setPlanLookup: (planLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>) => void;
};

export type PaywallContextValue = {
  minimumCredits?: number;
  paywallActions: PaywallActions;
  paywallState: PaywallState;
  track: TypedSegmentTrack;
};

export type PaywallProviderProps = {
  children: React.ReactNode;
  editProfileHookResult: ApiHookReturn<'useEditProfileMutation'>;
  handleMutation: MutationHandler;
  isFrictionlessUser?: boolean;
  isOpen: boolean;
  minimumCredits?: number;
  onClose: () => void;
  onComplete: () => Promise<void> | void;
  overridePromoCode?: string;
  planLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;
  retrieveClientHookResult: ApiHookReturn<'useRetrieveClientQuery'>;
  retrieveClientStatusHookResult: ApiHookReturn<'useRetrieveClientStatusQuery'>;
  retrievePromotionDetailsHookResult: ApiHookReturn<'useLazyRetrievePromotionDetailsQuery'>;
  shouldCompleteOnboardingOnClose: boolean;
  track: TypedSegmentTrack;
};

export const PaywallProvider = ({
  children,
  editProfileHookResult,
  handleMutation,
  isFrictionlessUser = false,
  isOpen,
  minimumCredits,
  onClose,
  onComplete,
  overridePromoCode,
  planLookup,
  retrieveClientHookResult,
  retrievePromotionDetailsHookResult,
  shouldCompleteOnboardingOnClose,
  track,
}: PaywallProviderProps) => {
  const [availablePlanLookup, setPlanLookup] = useState(planLookup);
  const [selectedPlanType, setSelectedPlan] = useState<MarketedMembershipPlanType>();
  const selectedPlanData: MaybeUndefined<MarketedMembershipPlan> = selectedPlanType
    ? (availablePlanLookup[selectedPlanType] as MarketedMembershipPlan)
    : undefined;
  const [selectedCreditsPackage, setSelectedCreditsPack] = useState<CreditPackage>();
  const [selectedPurchaseType, setSelectedPurchaseType] = useState<PurchaseType>();
  const [steps, setSteps] = useState<PaywallStep[]>([]);
  const [currentStep, setCurrentStep] = useState<PaywallStep>();
  const [paywallSessionId, setPaywallSessionId] = useState<string>();
  const currentStepIndex = currentStep ? steps.indexOf(currentStep) : 0;

  const skipPurchaseTypeStep = shouldCompleteOnboardingOnClose || isFrictionlessUser;

  useEffect(() => {
    setPlanLookup(planLookup);
  }, [planLookup]);

  useEffect(() => {
    if (skipPurchaseTypeStep) {
      setSelectedPurchaseType('Subscription');
    }
  }, [skipPurchaseTypeStep]);

  useEffect(() => {
    match(selectedPurchaseType)
      .with(P.union('Subscription', P.nullish), () => {
        if (skipPurchaseTypeStep) {
          setSteps(['selectSubscriptionPlan', 'checkout', 'success']);
          setCurrentStep('selectSubscriptionPlan');
        } else {
          setSteps(['selectPurchaseType', 'selectSubscriptionPlan', 'checkout', 'success']);
          setCurrentStep('selectPurchaseType');
        }
      })
      .with('ALaCarteCredits', () => {
        setSteps(['selectPurchaseType', 'checkout', 'success']);
        setCurrentStep('selectPurchaseType');
      })
      .exhaustive();
  }, [selectedPurchaseType, skipPurchaseTypeStep]);

  const firstPurchaseData = {
    selectedCreditsPackage,
    selectedPlanData,
    selectedPurchaseType: selectedPurchaseType,
  };

  const defaultPromoCode = useDefaultPromoCode({
    overridePromoCode,
    retrieveClientHookResult,
  });

  const promotionTranslationHook = usePromotionTranslation({
    defaultPromoCode,
    firstPurchaseData,
    handleMutation,
    retrievePromotionDetailsHookResult,
  });
  const blockAlaCarteSignup = useTempFeatureFlag('block_alacarte_signup_during_onboarding');
  const showDismissButton = !shouldCompleteOnboardingOnClose || !blockAlaCarteSignup;

  const canDecrement = currentStepIndex !== 0;
  const canIncrement = currentStepIndex < steps.length - 1;

  const incrementStep = useCallback(() => {
    if (canIncrement && currentStep !== 'success') {
      setCurrentStep(steps[currentStepIndex + 1]!);
    }
  }, [canIncrement, steps, currentStepIndex, currentStep]);

  const decrementStep = useCallback(() => {
    if (canDecrement) {
      setCurrentStep(() => steps[currentStepIndex - 1]!);
    }
  }, [canDecrement, steps, currentStepIndex]);

  // Reset function MUST be declared first before other functions use it
  const resetAllState = useCallback(() => {
    setCurrentStep(skipPurchaseTypeStep ? 'selectSubscriptionPlan' : 'selectPurchaseType');
    setSelectedPurchaseType(undefined);
    setSelectedPlan(undefined);
    setSelectedCreditsPack(undefined);
  }, [skipPurchaseTypeStep]);

  const onSelectPaymentType = useCallback(
    (option: OnConfirmPaymentTypeOption) => {
      match(option)
        .with({ type: 'ALaCarteCredits' }, ({ creditPackage }) => {
          setSelectedCreditsPack(creditPackage);
          setSelectedPurchaseType('ALaCarteCredits');
          track({
            data: {
              paywallSessionId,
              platform: 'web',
              purchaseType: 'ALaCarteCredits',
              step: currentStep,
            },
            event: 'purchaseTypeSelected',
            topic: 'paywall',
          });
        })
        .with({ type: 'Subscription' }, () => {
          setSelectedPurchaseType('Subscription');
          track({
            data: {
              paywallSessionId,
              platform: 'web',
              purchaseType: 'Subscription',
              step: currentStep,
            },
            event: 'purchaseTypeSelected',
            topic: 'paywall',
          });
        })
        .exhaustive();
    },
    [currentStep, paywallSessionId, track],
  );

  const onConfirmSubscriptionChoice = useCallback((selectedPlanType: MarketedMembershipPlanType) => {
    setSelectedPlan(selectedPlanType);
  }, []);

  const onSelectPlan = useCallback(
    (plan: MarketedMembershipPlanType) => {
      setSelectedPlan(plan);
      track({
        data: {
          paywallSessionId,
          planCode: availablePlanLookup[plan]?.planCode,
          platform: 'web',
          purchaseType: selectedPurchaseType,
          step: currentStep,
        },
        event: 'subscriptionPlanSelected',
        topic: 'paywall',
      });
    },
    [availablePlanLookup, currentStep, paywallSessionId, selectedPurchaseType, track],
  );

  const onFinish = useCallback(async () => {
    if (isFrictionlessUser) {
      track({
        data: {
          planCode: selectedPlanData?.planCode,
        },
        event: 'trialConversion',
        topic: 'auth',
      });
    }
    await onComplete();
    resetAllState();
  }, [isFrictionlessUser, onComplete, resetAllState, selectedPlanData, track]);

  const handleBackButton = useCallback(() => {
    if (currentStepIndex === 0) {
      track({
        data: {
          paywallSessionId,
          platform: 'web',
          reason: 'close',
          step: currentStep,
        },
        event: 'dismissed',
        topic: 'paywall',
      });
      onClose();
    } else {
      track({
        data: {
          paywallSessionId,
          platform: 'web',
          reason: 'back',
          step: currentStep,
        },
        event: 'dismissed',
        topic: 'paywall',
      });
      decrementStep();
    }
  }, [currentStep, currentStepIndex, decrementStep, onClose, paywallSessionId, track]);

  const onSkipPurchase = useCallback(() => {
    track({
      data: {
        paywallSessionId,
        platform: 'web',
        reason: 'skip',
        step: currentStep,
      },
      event: 'dismissed',
      topic: 'paywall',
    });

    if (shouldCompleteOnboardingOnClose) {
      handleMutation({
        mutation: () => editProfileHookResult[0]({ onboardingComplete: true }).unwrap(),
        onSuccess: onClose,
        showErrorToast: false,
        showSuccessToast: false,
      });
    } else {
      onClose();
    }
  }, [
    currentStep,
    shouldCompleteOnboardingOnClose,
    onClose,
    handleMutation,
    editProfileHookResult,
    paywallSessionId,
    track,
  ]);

  // Memoize actions to prevent infinite loops
  const paywallActions = useMemo(
    () =>
      ({
        navigation: {
          decrementStep,
          handleBackButton,
          incrementStep,
        },
        promotionTranslation: promotionTranslationHook.actions,
        purchase: {
          onConfirmSubscriptionChoice,
          onFinish,
          onSelectPaymentType,
          onSelectPlan,
          onSkipPurchase,
        },
        resetAllState,
        setPlanLookup,
      }) satisfies PaywallActions,
    [
      decrementStep,
      handleBackButton,
      incrementStep,
      promotionTranslationHook.actions,
      onConfirmSubscriptionChoice,
      onFinish,
      onSelectPaymentType,
      onSelectPlan,
      onSkipPurchase,
      resetAllState,
      setPlanLookup,
    ],
  );

  // Memoize state to prevent unnecessary re-renders
  const paywallState = useMemo(
    () =>
      ({
        navigation: {
          canDecrement,
          canIncrement,
          currentStep,
          currentStepIndex,
          paywallSessionId,
          shouldCompleteOnboardingOnClose,
          showDismissButton,
          steps,
        },
        promotionTranslation: promotionTranslationHook.state,
        purchase: {
          selectedCreditsPackage,
          selectedPlanData,
          selectedPlanType,
          selectedPurchaseType,
        },
      }) satisfies PaywallState,
    [
      canIncrement,
      currentStep,
      currentStepIndex,
      paywallSessionId,
      showDismissButton,
      steps,
      promotionTranslationHook.state,
      selectedCreditsPackage,
      selectedPlanData,
      selectedPlanType,
      selectedPurchaseType,
    ],
  );

  const contextValue: PaywallContextValue = useMemo(
    () => ({
      minimumCredits,
      paywallActions,
      paywallState,
      track,
    }),
    [minimumCredits, paywallActions, paywallState, track],
  );

  const hasTrackedOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && currentStep) {
      track({
        data: {
          paywallSessionId,
          platform: 'web',
          purchaseType: selectedPurchaseType,
          step: currentStep,
        },
        event: 'stepViewed',
        topic: 'paywall',
      });
    }
  }, [currentStep, isOpen, paywallSessionId, selectedPurchaseType, track]);

  // Track paywall modal open event when modal becomes visible
  useEffect(() => {
    if (isOpen && !hasTrackedOpenRef.current) {
      setPaywallSessionId(`${Date.now()}`);
      track({
        event: 'modalOpened',
        topic: 'paywall',
      });
      // eslint-disable-next-line fp/no-mutation
      hasTrackedOpenRef.current = true;
      return;
    }
    if (!isOpen) {
      setPaywallSessionId(undefined);
      // eslint-disable-next-line fp/no-mutation
      hasTrackedOpenRef.current = false;
    }
  }, [isOpen, track]);

  return <PaywallContext.Provider value={contextValue}>{children}</PaywallContext.Provider>;
};

export { PaywallContext };
